import {
	type ComponentType,
	createContext,
	type PropsWithChildren,
	type ReactNode,
	useContext,
} from 'react';

import { type ExtensionParams } from '@atlaskit/editor-common/extensions';
import { type MediaProvider } from '@atlaskit/editor-common/provider-factory';
import type { Auth } from '@atlaskit/media-core';
import type { UploadRejectionData } from '@atlaskit/media-picker';
import type { ThemeState } from '@atlaskit/tokens';

import { type ExtensionMode, type FeedbackType } from '../../types';

export interface ExperienceTracker<BaseStartParams = { analyticsSource: string }> {
	start: (params: BaseStartParams) => void;
	abort?: (reason: { reason: string }) => void;
	succeed: () => void;
	stopOnError: ({ error }: { error: Error }) => void;
}

type AiDescriptionStartParams = { analyticsSource: string; confluencePageId: string };
export type AiDescriptionExperienceTracker = ExperienceTracker<AiDescriptionStartParams>;

export interface ConfluenceDependenciesProviderProps {
	mode: ExtensionMode;
	extensionNode?: ExtensionParams<Record<string, any>>;
	contentId: string;
	children?: ReactNode;
}

interface Flag {
	id: string;
	title: string;
	description: string;
}

export interface ConfluenceDependenciesProviderResponse {
	cloudId: string;
	displayName: string;
	isAdminHubAIEnabled: boolean;
	orgId: string;
	userId: string;
	searchSessionId: string;
	submitError?: (error: Error) => void;
	markErrorAsHandled?: (error: Error) => void;
	currentDataRefHref?: string;
	usePremiumGate?: () => boolean;
	useSegmentationTargetGroups: ({
		nextCursor,
		initialGroupIds,
	}: {
		nextCursor?: string;
		initialGroupIds?: string[];
	}) => any;
	useAIDescription: ({
		spaceId,
		linkResourceType,
	}: {
		spaceId: string;
		linkResourceType: string;
	}) => any;
	ExtensionConfigUpsellLoader?: ComponentType<{ feature: string }>;
	errorBoundaryComponent?: ComponentType<PropsWithChildren<{}>>;
	experienceStartComponent?: ComponentType<{}>;
	errorDisplayComponent?: ComponentType<PropsWithChildren<{ error: Error }>>;
	experienceSuccessComponent: ComponentType<{}>;
	experienceStopComponent: ComponentType<{ error?: Error }>;
	smartCardProviderComponent: ComponentType<PropsWithChildren<{}>>;
	isTargetAudience: (targetGroupIds: string[] | undefined) => boolean;
	getUniquePageLoadId?: () => {
		isLivePage?: boolean | undefined;
		ufoTraceId?: string | undefined;
		ufoTraceType?: string | undefined;
		uuid: string | null;
		contentId: string | null;
		isInitial: boolean;
		isSSR: boolean;
		routingDuration: number;
	};
	useFeedbackCollector: (options: { source: string; type: FeedbackType }) => () => void;
	lazyEmojiComponentLoader: any;
	createRendererMediaProvider?: (options: {
		contentId: string;
		isExternalShare: boolean;
	}) => Promise<MediaProvider>;
	shortcutListener?: ComponentType<{ accelerator: string; listener: () => void }>;
	flags?: {
		showFlag?: (flag: Flag) => void;
		showErrorFlag?: (flag: Flag) => void;
	};

	// Custom search dependencies
	advancedSearchUrl: string;
	peopleDirectoryUrl: string;

	// Config panel media upload
	createEditorMediaProvider?: (options: {
		contentId: string;
		isExternalShare: boolean;
		contentType: string;
		onUploadRejection?: ((data: UploadRejectionData) => boolean) | undefined;
	}) => Promise<MediaProvider>;
	getSavedThemeLocal: () => Partial<ThemeState>;
	mediaUploadExperienceTracker?: ExperienceTracker;
	aiDescriptionExperienceTracker?: AiDescriptionExperienceTracker;
	useAnalyticsActions?: {
		[key: string]: any;
	};
	isCompanyHub: boolean;
}

export const ConfluenceDependenciesContext = createContext<ConfluenceDependenciesProviderResponse>({
	experienceSuccessComponent: () => null,
	experienceStopComponent: () => null,
	createRendererMediaProvider: undefined,
	cloudId: '',
	searchSessionId: '',
	displayName: '',
	isAdminHubAIEnabled: false,
	orgId: '',
	userId: '',
	advancedSearchUrl: '',
	peopleDirectoryUrl: '',
	flags: undefined,
	shortcutListener: undefined,
	useFeedbackCollector: () => () => {},
	useSegmentationTargetGroups: () => {},
	useAIDescription: () => {},
	errorDisplayComponent: () => null,
	smartCardProviderComponent: () => null,
	submitError: () => {},
	markErrorAsHandled: () => {},
	usePremiumGate: () => false,
	ExtensionConfigUpsellLoader: () => null,
	createEditorMediaProvider: () =>
		Promise.resolve({
			viewMediaClientConfig: {
				authProvider: async () => ({}) as Auth,
			},
			uploadMediaClientConfig: {
				authProvider: async () => ({}) as Auth,
			},
		} as MediaProvider),
	mediaUploadExperienceTracker: undefined,
	aiDescriptionExperienceTracker: undefined,
	lazyEmojiComponentLoader: () => null,
	currentDataRefHref: '',
	getSavedThemeLocal: () => ({}),
	getUniquePageLoadId: () => ({
		uuid: '',
		contentId: '',
		isInitial: false,
		isSSR: false,
		routingDuration: 0,
	}),
	isCompanyHub: false,
	isTargetAudience: () => false,
});

export function useProductDependencies(): ConfluenceDependenciesProviderResponse {
	const ctx = useContext<ConfluenceDependenciesProviderResponse>(ConfluenceDependenciesContext);
	if (!ctx) {
		throw new Error('useProductDependencies must be used within ConfluenceDependenciesProvider');
	}
	return ctx;
}
